// src/components/chat/TeamChatPanel.jsx — Channel Feed, DMs, PlatinumBot, Reactions & Composer
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Send, Bot, Plus, Hash, CornerUpLeft,
  Smile, Trash2, Edit2, X, Lock, Users, Check
} from "lucide-react";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

const REACTION_KEYS = ["👍", "❤️", "😂", "🔥", "💀", "👏", "🎉"];
const SLASH_COMMANDS = [
  { cmd: "/joke", desc: "Tapori joke" },
  { cmd: "/hype", desc: "Motivation booster" },
  { cmd: "/standup", desc: "Daily standup prompt" },
  { cmd: "/trivia", desc: "Car & JDM trivia" },
  { cmd: "/shayari", desc: "Chai & leads shayari" },
  { cmd: "/fortune", desc: "Kismat ka haal" },
  { cmd: "/help", desc: "Command guide" }
];

function formatTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const y = new Date();
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function renderBodyWithMentions(text) {
  if (!text) return "";
  const parts = text.split(/(@[a-zA-Z0-9_-]+)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("@")) {
      return (
        <span key={idx} className="chat-mention-pill">
          {part}
        </span>
      );
    }
    return part;
  });
}

export default function TeamChatPanel() {
  const { user } = useAuth();
  const {
    channels,
    activeChannelId,
    setActiveChannelId,
    messages,
    loadingMessages,
    sendMessage,
    toggleReaction,
    deleteMessage,
    editMessage,
    startTyping,
    stopTyping,
    typingUsers,
    createChannel,
    deleteChannel
  } = useChat();

  const [inputVal, setInputVal] = useState("");
  const [replyTo, setReplyTo] = useState(null); // { id, body, senderName }
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showNewChannelModal, setShowNewChannelModal] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelType, setNewChannelType] = useState("public"); // 'public' | 'private'
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // CRM Employees for mentions & group selection
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get("/employees").then(res => {
      if (Array.isArray(res.data)) setEmployees(res.data);
    }).catch(() => {});
  }, []);

  // @ Mention Autocomplete
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const textareaRef = useRef(null);

  const activeChannel = channels.find(c => c.id === activeChannelId) || channels[0];

  const canDeleteChannel = activeChannel && 
    activeChannel.name !== "general" && 
    activeChannel.id !== 1 && 
    (user?.role === "Admin" || String(activeChannel.created_by) === String(user?.id) || activeChannel.type === "dm");

  const handleDeleteChannel = async () => {
    if (!activeChannel) return;
    const channelLabel = activeChannel.type === "dm" 
      ? `conversation with ${activeChannel.displayName || "user"}`
      : `#${activeChannel.displayName || activeChannel.name}`;

    if (!window.confirm(`Are you sure you want to delete ${channelLabel}? All messages will be permanently deleted.`)) {
      return;
    }
    await deleteChannel(activeChannel.id);
  };

  // Mention Candidates Filter
  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    const list = [];

    if ("platinumbot".includes(q) || "bot".includes(q)) {
      list.push({ id: "bot", name: "PlatinumBot", role: "AI Assistant", isBot: true });
    }

    employees.forEach(emp => {
      if (emp.name && emp.name.toLowerCase().includes(q)) {
        list.push({ id: emp.id, name: emp.name, role: emp.role, onlineStatus: emp.onlineStatus });
      }
    });

    return list.slice(0, 8);
  }, [mentionQuery, employees]);

  const selectMention = (candidate) => {
    const textarea = textareaRef.current;
    const cursor = textarea ? textarea.selectionStart : inputVal.length;
    const textBefore = inputVal.slice(0, cursor);
    const textAfter = inputVal.slice(cursor);
    const replacedBefore = textBefore.replace(/@([a-zA-Z0-9_-]*)$/, `@${candidate.name} `);
    setInputVal(replacedBefore + textAfter);
    setMentionQuery(null);
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPos = replacedBefore.length;
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typingUsers]);

  // Handle typing debounce & detect @ mention
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputVal(val);
    if (!activeChannelId) return;

    startTyping(activeChannelId);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(activeChannelId);
    }, 1500);

    // Detect @ typing before cursor
    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const match = textBefore.match(/@([a-zA-Z0-9_-]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!inputVal.trim() || !activeChannelId) return;

    const body = inputVal.trim();
    setInputVal("");
    setReplyTo(null);
    setMentionQuery(null);
    stopTyping(activeChannelId);
    await sendMessage(body, replyTo?.id);
  };

  const handleKeyDown = (e) => {
    // Navigate mention popup if open
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex(prev => (prev + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex(prev => (prev - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        selectMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSaveEdit = async (msgId) => {
    if (!editVal.trim()) return;
    await editMessage(msgId, editVal.trim());
    setEditingMsgId(null);
  };

  const handleCreateNewChannel = async (e) => {
    e.preventDefault();
    if (!newChannelName.trim()) return;
    try {
      await createChannel(newChannelName.trim(), newChannelType, selectedMemberIds);
      setNewChannelName("");
      setNewChannelType("public");
      setSelectedMemberIds([]);
      setMemberSearchQuery("");
      setShowNewChannelModal(false);
    } catch (_) {}
  };

  // Group messages with day dividers
  let lastDay = "";

  return (
    <div className="team-chat-container">
      {/* ── 1. Channels & DMs Sidebar ── */}
      <div className="team-chat-nav">
        <div className="team-chat-nav-section">
          <span>Channels</span>
          <button
            onClick={() => setShowNewChannelModal(true)}
            style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: "2px" }}
            title="Create Channel"
          >
            <Plus size={13} />
          </button>
        </div>

        {channels
          .filter(c => c.type !== "dm")
          .map(c => (
            <button
              key={c.id}
              className={`team-nav-item ${c.id === activeChannelId ? "active" : ""}`}
              onClick={() => setActiveChannelId(c.id)}
            >
              {c.type === "private" ? (
                <Lock size={12} className="channel-lock" style={{ marginRight: "4px" }} />
              ) : (
                <span className="channel-hash">#</span>
              )}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.displayName || c.name}</span>
              {Number(c.unreadCount) > 0 && (
                <span className="unread-badge">{c.unreadCount}</span>
              )}
            </button>
          ))}

        <div className="team-chat-nav-section" style={{ marginTop: "8px" }}>
          <span>Direct Messages</span>
        </div>

        {channels
          .filter(c => c.type === "dm")
          .map(c => (
            <button
              key={c.id}
              className={`team-nav-item ${c.id === activeChannelId ? "active" : ""}`}
              onClick={() => setActiveChannelId(c.id)}
            >
              <span className={`user-status-dot ${c.onlineStatus || "offline"}`} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.displayName || "DM"}</span>
              {Number(c.unreadCount) > 0 && (
                <span className="unread-badge">{c.unreadCount}</span>
              )}
            </button>
          ))}
      </div>

      {/* ── 2. Active Channel Feed ── */}
      <div className="team-chat-feed">
        {/* Header */}
        <div className="chat-feed-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="chat-feed-header-title">
            {activeChannel?.type === "dm" ? (
              <>
                <span className={`user-status-dot ${activeChannel?.onlineStatus || "offline"}`} />
                <span>{activeChannel?.displayName || "Direct Message"}</span>
              </>
            ) : (
              <>
                <Hash size={15} className="text-muted" />
                <span>{activeChannel?.displayName || activeChannel?.name || "general"}</span>
              </>
            )}
          </div>

          {canDeleteChannel && (
            <button
              className="chat-icon-btn"
              onClick={handleDeleteChannel}
              title={`Delete ${activeChannel?.type === "dm" ? "conversation" : "#" + (activeChannel?.name || "channel")}`}
              style={{ color: "var(--danger, #ff453a)" }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {/* Message Thread */}
        <div className="chat-messages-scroll" ref={scrollRef}>
          {loadingMessages && messages.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--muted)", padding: "20px", fontSize: "12.5px" }}>
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--muted)", margin: "auto", fontSize: "13px" }}>
              <div style={{ marginBottom: "6px", fontSize: "24px" }}>💬</div>
              No messages yet. Say hello or type <code>@PlatinumBot /joke</code>!
            </div>
          ) : (
            messages.map((msg) => {
              const msgDay = formatDateLabel(msg.created_at);
              const showDivider = msgDay !== lastDay;
              lastDay = msgDay;

              const isMe = msg.sender_id === user?.id;
              const isBot = Boolean(msg.is_bot);
              const isDeleted = Boolean(msg.deleted_at);

              return (
                <React.Fragment key={msg.id}>
                  {showDivider && (
                    <div className="chat-day-divider">
                      <span>{msgDay}</span>
                    </div>
                  )}

                  <div className={`chat-msg-row ${isBot ? "is-bot" : ""}`}>
                    {/* Avatar */}
                    <div className={`chat-msg-avatar ${isBot ? "bot" : ""}`}>
                      {isBot ? <Bot size={16} /> : (msg.senderName?.[0]?.toUpperCase() || "U")}
                    </div>

                    {/* Content */}
                    <div className="chat-msg-content">
                      <div className="chat-msg-head">
                        <span className="chat-msg-author">
                          {isBot ? "PlatinumBot" : (msg.senderName || "Unknown")}
                        </span>
                        {isBot && <span className="chat-msg-badge bot">Bot</span>}
                        {!isBot && msg.senderRole === "Admin" && (
                          <span className="chat-msg-badge admin">Admin</span>
                        )}
                        <span className="chat-msg-time">{formatTime(msg.created_at)}</span>
                      </div>

                      {/* Reply Reference if present */}
                      {msg.reply_to_body && (
                        <div className="chat-msg-reply-ref">
                          <CornerUpLeft size={11} />
                          <span><strong>{msg.reply_to_sender || "Member"}:</strong> {msg.reply_to_body.slice(0, 50)}</span>
                        </div>
                      )}

                      {/* Body or Edit Form */}
                      {editingMsgId === msg.id ? (
                        <div style={{ marginTop: "4px" }}>
                          <input
                            type="text"
                            value={editVal}
                            onChange={(e) => setEditVal(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "6px 8px",
                              borderRadius: "6px",
                              background: "var(--surface)",
                              color: "var(--text)",
                              border: "1px solid var(--primary)",
                              fontSize: "13px"
                            }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit(msg.id);
                              if (e.key === "Escape") setEditingMsgId(null);
                            }}
                          />
                          <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                            <button
                              onClick={() => handleSaveEdit(msg.id)}
                              style={{ padding: "2px 8px", fontSize: "11px", background: "var(--primary)", color: "#fff", border: "none", borderRadius: "4px" }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingMsgId(null)}
                              style={{ padding: "2px 8px", fontSize: "11px", background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: "4px" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`chat-msg-body ${isDeleted ? "deleted" : ""}`}>
                          {isDeleted ? "This message was deleted" : renderBodyWithMentions(msg.body)}
                          {msg.edited_at && !isDeleted && (
                            <span style={{ fontSize: "10px", color: "var(--muted)", marginLeft: "6px" }}>(edited)</span>
                          )}
                        </div>
                      )}

                      {/* Reactions List */}
                      {msg.reactions && msg.reactions.length > 0 && !isDeleted && (
                        <div className="chat-msg-reactions">
                          {msg.reactions.map((r, i) => (
                            <button
                              key={i}
                              className={`reaction-pill ${r.hasReacted ? "user-reacted" : ""}`}
                              onClick={() => toggleReaction(msg.id, r.reaction_key)}
                            >
                              <span>{r.reaction_key}</span>
                              <span>{r.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Hover Quick Actions */}
                    {!isDeleted && (
                      <div className="chat-msg-hover-actions">
                        <button
                          className="msg-hover-btn"
                          title="Reply"
                          onClick={() => setReplyTo({ id: msg.id, body: msg.body, senderName: msg.senderName })}
                        >
                          <CornerUpLeft size={13} />
                        </button>
                        {REACTION_KEYS.slice(0, 3).map((emoji) => (
                          <button
                            key={emoji}
                            className="msg-hover-btn"
                            onClick={() => toggleReaction(msg.id, emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                        {isMe && !isBot && (
                          <>
                            <button
                              className="msg-hover-btn"
                              title="Edit"
                              onClick={() => {
                                setEditingMsgId(msg.id);
                                setEditVal(msg.body);
                              }}
                            >
                              <Edit2 size={12} />
                            </button>
                            <button
                              className="msg-hover-btn"
                              title="Delete"
                              onClick={() => deleteMessage(msg.id)}
                            >
                              <Trash2 size={12} style={{ color: "var(--danger)" }} />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}

          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div style={{ fontSize: "11.5px", color: "var(--muted)", fontStyle: "italic", padding: "4px 8px" }}>
              {typingUsers.map(u => u.userName).join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing...
            </div>
          )}
        </div>

        {/* ── 3. Composer ── */}
        <div className="chat-composer">
          {/* Reply Banner */}
          {replyTo && (
            <div className="reply-banner">
              <span>Replying to <strong>{replyTo.senderName}</strong>: "{replyTo.body.slice(0, 40)}..."</span>
              <button
                onClick={() => setReplyTo(null)}
                style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Autocomplete Popup for @mentions */}
          {mentionQuery !== null && mentionCandidates.length > 0 && (
            <div className="chat-mention-popup">
              {mentionCandidates.map((cand, idx) => (
                <button
                  key={cand.id}
                  type="button"
                  className={`chat-mention-item ${idx === mentionIndex ? "selected" : ""}`}
                  onClick={() => selectMention(cand)}
                >
                  {cand.isBot ? (
                    <Bot size={14} style={{ color: "var(--primary)" }} />
                  ) : (
                    <Users size={14} style={{ color: "var(--muted)" }} />
                  )}
                  <span style={{ fontWeight: 600 }}>{cand.name}</span>
                  {cand.role && (
                    <span style={{ fontSize: "11px", color: "var(--muted)", marginLeft: "auto" }}>
                      {cand.role}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Input Box */}
          <form className="chat-composer-box" onSubmit={handleSend} style={{ position: "relative" }}>
            <textarea
              ref={textareaRef}
              className="chat-textarea"
              placeholder={`Message #${activeChannel?.displayName || activeChannel?.name || "general"}...`}
              rows={1}
              value={inputVal}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />

            <button
              type="button"
              className="chat-icon-btn"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              style={{ width: "30px", height: "30px" }}
              title="Add Reaction"
            >
              <Smile size={16} />
            </button>

            <button
              type="submit"
              className="chat-send-btn"
              disabled={!inputVal.trim()}
              title="Send Message"
            >
              <Send size={15} />
            </button>
          </form>

          {/* Quick Emoji Strip */}
          {showEmojiPicker && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                padding: "6px 8px",
                marginTop: "6px",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "8px"
              }}
            >
              {REACTION_KEYS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  style={{ background: "none", border: "none", fontSize: "16px", cursor: "pointer" }}
                  onClick={() => {
                    setInputVal(prev => prev + " " + emoji);
                    setShowEmojiPicker(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/* Quick Slash Commands / Bot Mention bar */}
          <div className="chat-quick-hints">
            <button
              type="button"
              className="chat-hint-chip"
              onClick={() => setInputVal("@PlatinumBot ")}
            >
              <Bot size={11} /> @PlatinumBot
            </button>
            {SLASH_COMMANDS.map((sc) => (
              <button
                key={sc.cmd}
                type="button"
                className="chat-hint-chip"
                onClick={() => setInputVal(sc.cmd + " ")}
                title={sc.desc}
              >
                {sc.cmd}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── New Channel Modal ── */}
      {showNewChannelModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px"
          }}
          onClick={() => setShowNewChannelModal(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "420px",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "14px",
              padding: "20px",
              boxShadow: "0 16px 40px rgba(0,0,0,0.6)"
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Create Channel or Team Group</span>
              <button
                type="button"
                onClick={() => setShowNewChannelModal(false)}
                style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: "4px" }}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateNewChannel}>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", fontSize: "11.5px", fontWeight: 600, color: "var(--muted)", marginBottom: "4px" }}>
                  CHANNEL NAME
                </label>
                <input
                  type="text"
                  placeholder="e.g. sales-tokyo, logistics-alpha"
                  value={newChannelName}
                  onChange={e => setNewChannelName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "8px",
                    background: "var(--surface-2)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                    fontSize: "13px"
                  }}
                  autoFocus
                />
              </div>

              {/* Type Switcher */}
              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "11.5px", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>
                  CHANNEL PRIVACY
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={() => setNewChannelType("public")}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "8px",
                      border: `1px solid ${newChannelType === "public" ? "var(--primary)" : "var(--border)"}`,
                      background: newChannelType === "public" ? "rgba(10,132,255,0.12)" : "transparent",
                      color: newChannelType === "public" ? "var(--primary)" : "var(--text)",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <Hash size={13} /> Public Channel
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewChannelType("private")}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "8px",
                      border: `1px solid ${newChannelType === "private" ? "var(--primary)" : "var(--border)"}`,
                      background: newChannelType === "private" ? "rgba(10,132,255,0.12)" : "transparent",
                      color: newChannelType === "private" ? "var(--primary)" : "var(--text)",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <Lock size={13} /> Private Group
                  </button>
                </div>
              </div>

              {/* Private Group Member Checklist */}
              {newChannelType === "private" && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "var(--muted)" }}>
                      SELECT TEAM MEMBERS ({selectedMemberIds.length})
                    </label>
                  </div>
                  <input
                    type="text"
                    placeholder="Search employees by name or role..."
                    value={memberSearchQuery}
                    onChange={e => setMemberSearchQuery(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "7px 10px",
                      borderRadius: "6px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      fontSize: "12px",
                      marginBottom: "8px"
                    }}
                  />
                  <div
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "4px"
                    }}
                  >
                    {employees
                      .filter(emp => emp.id !== user?.id)
                      .filter(emp => !memberSearchQuery || emp.name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) || emp.role?.toLowerCase().includes(memberSearchQuery.toLowerCase()))
                      .map(emp => {
                        const isSelected = selectedMemberIds.includes(emp.id);
                        return (
                          <div
                            key={emp.id}
                            onClick={() => {
                              setSelectedMemberIds(prev =>
                                isSelected ? prev.filter(id => id !== emp.id) : [...prev, emp.id]
                              );
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "6px 8px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              background: isSelected ? "var(--surface-2)" : "transparent"
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <div
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "4px",
                                  border: `1.5px solid ${isSelected ? "var(--primary)" : "var(--muted)"}`,
                                  background: isSelected ? "var(--primary)" : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff"
                                }}
                              >
                                {isSelected && <Check size={12} strokeWidth={3} />}
                              </div>
                              <span style={{ fontSize: "12.5px", fontWeight: 500 }}>{emp.name}</span>
                              <span style={{ fontSize: "11px", color: "var(--muted)" }}>({emp.role})</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button
                  type="button"
                  onClick={() => setShowNewChannelModal(false)}
                  style={{ padding: "7px 14px", borderRadius: "8px", background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", fontSize: "12.5px", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newChannelName.trim()}
                  style={{ padding: "7px 16px", borderRadius: "8px", background: "var(--primary)", color: "#fff", border: "none", fontSize: "12.5px", fontWeight: 600, cursor: "pointer" }}
                >
                  Create {newChannelType === "private" ? "Group" : "Channel"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
